import React from 'react';

import GreenCheckMarkBadge from 'src/assets/green-check-mark-badge';
import useUI from 'src/hooks/use-ui';
import { CashBackDetails, CashBackTermsLink, bankStatementTerms } from 'src/payments/components/cash-back-details';
import {
  PdpIncentiveAccordionBanner,
  HeadlineRow,
  HeadlineText,
  LinkBankButton,
  BankLogoStripPlaceholder,
} from 'src/payments/components/incentive-accordion-styles/incentive-accordion-styles';
import { useCashBackSurfaceGate } from 'src/payments/hooks/use-cash-back-surface-gate';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { useIncentiveExposure } from 'src/payments/hooks/use-incentive-exposure';
import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { Typography } from 'src/typography';

const EXPOSURE_SOURCE = 'product_description';

const BankLogosMarqueeVertical = React.lazy(() =>
  import('src/payments/components/bank-logos-marquee-vertical').then((module) => ({
    default: module.BankLogosMarqueeVertical,
  }))
);

// The CTA lives inside the accordion summary's mainContent; the summary itself is the disclosure
// control, so every interaction path (click, Space, Enter) must stop propagation to avoid
// toggling the accordion when the CTA is used.
const stopSummaryToggle = (event: React.SyntheticEvent): void => event.stopPropagation();

export const PdpCashBackAccordion = (): JSX.Element | null => {
  const { showCashBack, cashBackValue } = useCashBackSurfaceGate('productDescription');
  const { isEnrolledDutchiePay } = useDutchiePay();
  const { userType } = useIncentives();
  const UI = useUI();
  const { openDutchiePay } = useOpenDutchiePay({
    enrollmentSource: `pdp-cash-back-${UI?.variant}`,
  });

  useIncentiveExposure({
    incentiveValue: showCashBack && !UI?.isKiosk ? cashBackValue : undefined,
    userType,
    isCashBack: true,
    source: EXPOSURE_SOURCE,
    rewardType: 'cash_back',
  });

  // No signup at kiosk, so the surface suppresses there regardless of flag/eligibility.
  if (UI?.isKiosk || !showCashBack || cashBackValue === undefined) {
    return null;
  }

  const value = cashBackValue;

  const mainContent = isEnrolledDutchiePay ? (
    <HeadlineText>
      <strong>Bank linked</strong>
      <br />
      Pay by Bank at checkout to claim ${value} back. <u>Terms Apply.</u>
    </HeadlineText>
  ) : (
    <HeadlineRow>
      <HeadlineText>
        <strong>${value} cash back</strong> on your first order paid by bank. <u>Terms Apply.</u>
      </HeadlineText>
      <LinkBankButton
        type='button'
        onClick={(event) => {
          stopSummaryToggle(event);
          openDutchiePay();
        }}
        onKeyDown={stopSummaryToggle}
        onKeyUp={stopSummaryToggle}
      >
        Link bank
      </LinkBankButton>
    </HeadlineRow>
  );

  const toggleContent = isEnrolledDutchiePay ? (
    <Typography.Label size='small'>
      Choose Pay now at checkout &amp; complete your Pay by Bank payment to claim ${value} cash back.{' '}
      {bankStatementTerms(value)}
      <br />
      <CashBackTermsLink>View full terms</CashBackTermsLink>
    </Typography.Label>
  ) : (
    <CashBackDetails value={value} />
  );

  return (
    <PdpIncentiveAccordionBanner
      marginBottom='16px'
      borderRadius='var(--dcm-pdp-incentive-radius, 4px)'
      graphic={
        isEnrolledDutchiePay ? (
          <GreenCheckMarkBadge />
        ) : (
          <React.Suspense fallback={<BankLogoStripPlaceholder />}>
            <BankLogosMarqueeVertical />
          </React.Suspense>
        )
      }
      graphicWidth={isEnrolledDutchiePay ? undefined : '20px'}
      graphicFullHeight={!isEnrolledDutchiePay}
      mainContent={mainContent}
      toggleContent={toggleContent}
    />
  );
};
